import React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Dialog - Komponent modala w stylu Radix Themes
 * 
 * API zgodne z @radix-ui/themes
 * - Rozmiary: "1" | "2" | "3" | "4" (szerokość)
 * - Accessibility (a11y) - keyboard navigation, focus trap, aria
 * - Animacje fade/scale
 * 
 * @example
 * ```tsx
 * <Dialog>
 *   <DialogTrigger asChild>
 *     <Button>Otwórz</Button>
 *   </DialogTrigger>
 *   <DialogContent size="3">
 *     <DialogHeader>
 *       <DialogTitle>Tytuł</DialogTitle>
 *       <DialogDescription>Opis</DialogDescription>
 *     </DialogHeader>
 *     <div>Zawartość</div>
 *     <DialogFooter>
 *       <DialogClose asChild>
 *         <Button variant="outline">Anuluj</Button>
 *       </DialogClose>
 *       <Button>Zapisz</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 * ```
 */

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

/**
 * DialogOverlay - Ciemne tło za modalem
 */
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50",
      "bg-[rgba(13,13,13,0.95)]",
      "backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * DialogContent - Główna zawartość modala
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: "1" | "2" | "3" | "4"
  }
>(({ className, size = "3", children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50",
        "translate-x-[-50%] translate-y-[-50%]",
        "w-full",
        "max-h-[90vh] overflow-y-auto",
        "bg-[var(--dark-2)]",
        "border border-[var(--dark-a6)]",
        "rounded-[var(--radius-xl)]",
        "shadow-[0_8px_32px_0_rgba(0,0,0,0.4)]",
        "duration-200",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
        "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        "focus:outline-none",
        // Rozmiary
        size === "1" && "max-w-xs p-[var(--space-5)]",
        size === "2" && "max-w-sm p-[var(--space-6)]",
        size === "3" && "max-w-lg p-[var(--space-7)]",
        size === "4" && "max-w-2xl p-[var(--space-8)]",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        className={cn(
          "absolute right-[var(--space-4)] top-[var(--space-4)]",
          "rounded-[var(--radius-sm)]",
          "p-[var(--space-2)]",
          "text-[var(--text-white-80)]",
          "transition-colors",
          "hover:bg-[var(--dark-3)] hover:text-[var(--text-white-95)]",
          "focus:outline-none focus:ring-2 focus:ring-[var(--pink-9)] focus:ring-offset-2",
          "disabled:pointer-events-none",
          "data-[state=open]:bg-[var(--dark-3)]"
        )}
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Zamknij</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/**
 * DialogHeader - Nagłówek modala
 */
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col gap-[var(--space-2)]",
      "mb-[var(--space-6)]",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

/**
 * DialogFooter - Stopka modala (akcje)
 */
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end",
      "gap-[var(--space-3)]",
      "mt-[var(--space-6)]",
      "pt-[var(--space-6)]",
      "border-t border-[var(--dark-a4)]",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

/**
 * DialogTitle - Tytuł modala
 */
const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-[var(--font-size-7)]",
      "font-[var(--font-weight-bold)]",
      "leading-[var(--line-height-2)]",
      "tracking-[var(--letter-spacing-tight)]",
      "text-[var(--text-white-95)]",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

/**
 * DialogDescription - Opis modala
 */
const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      "text-[var(--font-size-2)]",
      "leading-[var(--line-height-4)]",
      "text-[var(--text-white-70)]",
      className
    )}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}

